import { useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Users, Snowflake, ArrowUpRight } from "lucide-react";

export default function Transportation() {
  const [sp, setSp] = useSearchParams();
  const activeCat = sp.get("category") || "all";

  const { data: cats = [] } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/vehicle-categories", { params: { is_active: true } }).then((r) => r.data.items) });
  const { data: vehicles = [] } = useQuery({ queryKey: ["vehicles"], queryFn: () => api.get("/vehicles", { params: { is_active: true, limit: 100 } }).then((r) => r.data.items) });

  const catBySlug = useMemo(() => Object.fromEntries(cats.map((c) => [c.slug, c])), [cats]);
  const catById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const list = activeCat === "all" ? vehicles : vehicles.filter((v) => catById[v.category_id]?.slug === activeCat);

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs uppercase tracking-[0.24em] text-amber-600 mb-4">Transportation</div>
        <h1 className="font-display text-5xl sm:text-6xl text-slate-950 leading-none">Our boutique fleet.</h1>
        <p className="mt-6 text-slate-600 max-w-2xl">Every vehicle in our fleet is professionally serviced and paired with a hospitality-trained chauffeur.</p>

        <div className="mt-10 flex flex-wrap gap-2">
          <button onClick={() => setSp({})} className={`px-4 py-2 rounded-full text-sm border ${activeCat === "all" ? "bg-slate-950 text-white border-slate-950" : "border-slate-300 text-slate-700 hover:border-slate-500"}`} data-testid="cat-filter-all">All</button>
          {cats.map((c) => (
            <button key={c.id} onClick={() => setSp({ category: c.slug })} className={`px-4 py-2 rounded-full text-sm border ${activeCat === c.slug ? "bg-slate-950 text-white border-slate-950" : "border-slate-300 text-slate-700 hover:border-slate-500"}`} data-testid={`cat-filter-${c.slug}`}>
              {c.name}
            </button>
          ))}
        </div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {list.map((v) => (
            <Link to={`/transportation/${v.slug}`} key={v.id} className="group rounded-2xl overflow-hidden bg-white border border-slate-200 hover:-translate-y-1 transition-transform" data-testid={`vehicle-${v.slug}`}>
              <div className="aspect-[4/3] overflow-hidden">
                <img src={v.image_url} alt={v.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-600">{catById[v.category_id]?.name}</div>
                <div className="mt-2 font-display text-2xl text-slate-950 group-hover:text-amber-700">{v.name}</div>
                <div className="mt-3 flex items-center gap-4 text-sm text-slate-500">
                  <span className="inline-flex items-center gap-1"><Users className="w-4 h-4" /> {v.seats}</span>
                  {v.ac && <span className="inline-flex items-center gap-1"><Snowflake className="w-4 h-4" /> AC</span>}
                </div>
                <div className="mt-5 flex items-center justify-between">
                  <div className="text-sm text-slate-500">{v.price_hint}</div>
                  <span className="text-sm text-slate-700 group-hover:text-amber-700 inline-flex items-center gap-1">Details <ArrowUpRight className="w-4 h-4" /></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
        {list.length === 0 && <div className="mt-16 text-center text-slate-500">No vehicles in this category.</div>}
      </div>
    </div>
  );
}
